import {faPlus, faFileUpload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Group} from '@mantine/core';
import {RigsService} from '../api/generated/RigsService';
import {
  PagedDataTableConfig,
  renderEditButton,
  usePagedDataTable,
} from '../hooks/paged-data-table/use-paged-data-table';
import {BasicPage} from '../components/basic-page';
import {BreadcrumbPart} from '../components/page-title';
import {Link, useParams} from 'react-router-dom';
import {ROUTES} from '../routes';
import {useCallback, useMemo, useRef} from 'react';
import {useNotification} from '../hooks/use-notifications';
import {notify} from '../hooks/use-subscription';

export const RigListing = () => {
  const {partnerSiteId} = useParams<{partnerSiteId: string}>();
  const partnerSiteIdNum = Number(partnerSiteId);
  const notifications = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const breadcrumbs: BreadcrumbPart[] = useMemo(
    () => [
      {title: 'S/N Tracking', href: ROUTES.ADMIN.SERIAL_NUMBER_TRACKING.INDEX.path},
      {title: 'Tracked Serial Numbers', href: ROUTES.ADMIN.SERIAL_NUMBER_TRACKING.DETAIL.buildPath({id: partnerSiteIdNum})},
      {title: 'Rigs'},
    ],
    [partnerSiteIdNum],
  );

  // Create a service function bound to the partnerSiteId
  const fetchRigs = useCallback(
    (params: Omit<Parameters<typeof RigsService.getAll>[0], 'partnerSiteId'>) => {
      return RigsService.getAll({...params, partnerSiteId: partnerSiteIdNum});
    },
    [partnerSiteIdNum],
  );

  const onImportCsv = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const response = await RigsService.importCsv({
        partnerSiteId: partnerSiteIdNum,
        file,
      });

      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (response.hasErrors) {
        notifications.errors(response.errors);
        return;
      }

      const result = response.data;
      const parts = [`Created ${result?.created ?? 0} rig(s)`];
      if (result?.skipped) parts.push(`${result.skipped} skipped`);
      notifications.success(parts.join(', '));

      if (result?.errors?.length) {
        result.errors.forEach((err) => notifications.warning(err));
      }

      // Refresh the listing
      notify('rig-listing', undefined);
    },
    [partnerSiteIdNum, notifications],
  );

  const tableConfig = useMemo(
    () =>
      PagedDataTableConfig(fetchRigs, {
        columns: [
          {
            accessor: '',
            title: '',
            render: (item) =>
              renderEditButton({
                item: {...item, id: Number(item.id)},
                descriptor: 'rig',
                route: ROUTES.ADMIN.RIGS.DETAIL.buildPath({partnerSiteId: partnerSiteIdNum, id: Number(item.id)}),
              }),
          },
          {
            accessor: 'name',
            title: 'Name',
            sortable: true,
          },
          {
            accessor: 'notificationEmails',
            title: 'Notification Emails',
          },
        ],
        defaultSort: {
          column: 'name',
          direction: 'ASC',
        },
        searchFieldNames: ['name'],
        refetchEvent: 'rig-listing',
      }),
    [fetchRigs, partnerSiteIdNum],
  );

  const runtimeConfig = useMemo(
    () => ({
      renderActions: (
        <Group gap='sm'>
          <input
            type='file'
            ref={fileInputRef}
            accept='.csv'
            style={{display: 'none'}}
            onChange={onImportCsv}
          />
          <Button variant='outline' onClick={() => fileInputRef.current?.click()}>
            <Group gap='xs'>
              <FontAwesomeIcon icon={faFileUpload} /> Import CSV
            </Group>
          </Button>
          <Button component={Link} to={ROUTES.ADMIN.RIGS.CREATE.buildPath({partnerSiteId: partnerSiteIdNum})}>
            <Group gap='xs'>
              <FontAwesomeIcon icon={faPlus} /> New Rig
            </Group>
          </Button>
        </Group>
      ),
    }),
    [partnerSiteIdNum, onImportCsv],
  );

  const pagedDataTable = usePagedDataTable(fetchRigs, tableConfig, runtimeConfig);

  return <BasicPage title={breadcrumbs}>{pagedDataTable}</BasicPage>;
};
